// "Completar con IA": elegir qué pedir, y revisar lo que propuso —
// ADR-2026-09-08-ficha-con-ia y ADR-2026-09-08-acciones-en-lote.
//
// Esta pantalla es la razón por la que la función es segura. La IA no escribe en
// la ficha: propone, y acá se ve qué propuso al lado de lo que ya había, campo
// por campo, con la casilla de cada uno. Dos de estos valores —el peso y las
// medidas— deciden lo que el correo le cobra al cliente, así que entrar solos no
// es una opción.
//
// Sirve para UN producto o para VARIOS con el mismo código: el lote es esto
// mismo repetido, y la revisión de cada producto es el mismo bloque. Separarlos
// habría dejado dos pantallas que muestran lo mismo y se van despegando.
import { boton } from './botones';
import { useEffect, useMemo, useState } from 'react';
import { Check, ChevronRight, FolderPlus, Loader2, Package, Sparkles } from 'lucide-react';
import { SheetLateral } from '../../components/SheetLateral';
import { caminoDe, type CategoriaPlana } from './arbolDeCategorias';
import {
  NOMBRE_DE_CAMPO,
  mensajesDeEspera,
  tieneAlgo,
  type CampoIa,
  type SugerenciaDeFicha,
} from './fichaIa';

/** Qué se pide por defecto: todo lo que la IA puede deducir mirando las fotos. */
/** Todo lo que el destino admita: el usuario destilda lo que no quiera. */
function porDefecto(ofrecidos: readonly CampoIa[]): CampoIa[] {
  return [...ofrecidos];
}

/** Cuánto dura cada mensaje de la espera. Lo suficiente para leerlo sin apurarse. */
const MS_POR_MENSAJE = 2_200;

/**
 * El texto que rota mientras la IA trabaja.
 *
 * Da vueltas en vez de quedarse en el último: la espera puede durar más que la
 * lista, y un texto congelado se lee como que se colgó. Volver a empezar dice
 * "sigue trabajando", que es lo que está pasando.
 */
function useMensajeDeEspera(campos: readonly CampoIa[], activo: boolean): string {
  const mensajes = useMemo(() => mensajesDeEspera(campos), [campos]);
  const [i, setI] = useState(0);

  useEffect(() => {
    if (!activo) {
      setI(0);
      return;
    }
    const t = window.setInterval(() => setI((n) => (n + 1) % mensajes.length), MS_POR_MENSAJE);
    return () => window.clearInterval(t);
  }, [activo, mensajes.length]);

  return mensajes[i % mensajes.length] ?? mensajes[0]!;
}

/** Lo que la ficha de un producto tiene hoy, para poder comparar. */
export interface EstadoActual {
  descripcion?: string | null;
  marca?: string | null;
  tags?: string | null;
  seoTitulo?: string | null;
  peso?: string | null;
  categorias: readonly string[];
}

export interface ProductoParaIa {
  id: string;
  nombre: string;
  actual: EstadoActual;
}

function Casilla({
  puesta,
  onCambiar,
  titulo,
  children,
}: {
  puesta: boolean;
  onCambiar: (v: boolean) => void;
  titulo: string;
  children?: React.ReactNode;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-neutral-200 py-3 px-3 transition-colors hover:bg-neutral-50">
      <input
        type="checkbox"
        checked={puesta}
        onChange={(e) => onCambiar(e.target.checked)}
        className="mt-0.5 h-4 w-4 shrink-0 rounded border-neutral-300 accent-[#1644EB]"
      />
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium text-brand-black">{titulo}</span>
        {children}
      </span>
    </label>
  );
}

/** Lo que había antes. Vacío se dice, no se deja en blanco. */
function Antes({ valor }: { valor?: string | null }) {
  return (
    <span className="mt-1 block text-[11px] text-neutral-400">
      Ahora: {valor?.trim() ? valor.trim().slice(0, 90) : 'vacío'}
    </span>
  );
}

/**
 * La revisión de UNA sugerencia. Es la pieza que se repite en el lote.
 *
 * La aceptación es por campo y por producto: en un lote, que la descripción de
 * uno esté bien no dice nada de la del otro.
 */
function RevisionDeSugerencia({
  sugerencia,
  actual,
  aceptados,
  onCambiar,
  categorias,
}: {
  sugerencia: SugerenciaDeFicha;
  actual: EstadoActual;
  aceptados: readonly CampoIa[];
  onCambiar: (campo: CampoIa, puesto: boolean) => void;
  categorias: CategoriaPlana[];
}) {
  const nombreDeCategoria = useMemo(
    () => (id: string) => caminoDe(categorias, id) || id,
    [categorias],
  );

  return (
    <div className="space-y-2">
      {sugerencia.descripcion && (
        <Casilla
          puesta={aceptados.includes('descripcion')}
          onCambiar={(v) => onCambiar('descripcion', v)}
          titulo="Descripción"
        >
          <span className="mt-1 block whitespace-pre-wrap text-xs text-neutral-600">
            {sugerencia.descripcion}
          </span>
          <Antes valor={actual.descripcion} />
        </Casilla>
      )}

      {sugerencia.marca && (
        <Casilla
          puesta={aceptados.includes('marca')}
          onCambiar={(v) => onCambiar('marca', v)}
          titulo="Marca"
        >
          <span className="mt-1 block text-xs text-neutral-600">{sugerencia.marca}</span>
          <Antes valor={actual.marca} />
        </Casilla>
      )}

      {sugerencia.tags && (
        <Casilla
          puesta={aceptados.includes('tags')}
          onCambiar={(v) => onCambiar('tags', v)}
          titulo="Etiquetas"
        >
          <span className="mt-1 block text-xs text-neutral-600">{sugerencia.tags}</span>
          <Antes valor={actual.tags} />
        </Casilla>
      )}

      {(sugerencia.seoTitulo || sugerencia.seoDescripcion) && (
        <Casilla
          puesta={aceptados.includes('seo')}
          onCambiar={(v) => onCambiar('seo', v)}
          titulo="SEO"
        >
          {sugerencia.seoTitulo && (
            <span className="mt-1 block text-xs text-neutral-600">{sugerencia.seoTitulo}</span>
          )}
          {sugerencia.seoDescripcion && (
            <span className="mt-1 block text-[11px] text-neutral-500">
              {sugerencia.seoDescripcion}
            </span>
          )}
          <Antes valor={actual.seoTitulo} />
        </Casilla>
      )}

      {Boolean(sugerencia.categoriaIds?.length || sugerencia.categoriasNuevas?.length) && (
        <Casilla
          puesta={aceptados.includes('categorias')}
          onCambiar={(v) => onCambiar('categorias', v)}
          titulo="Categorías"
        >
          {sugerencia.categoriaIds && sugerencia.categoriaIds.length > 0 && (
            <span className="mt-1 block text-xs text-neutral-600">
              {sugerencia.categoriaIds.map(nombreDeCategoria).join(' · ')}
            </span>
          )}
          {/* Las que no existen se marcan como tales: aceptar esto no es solo
              etiquetar el producto, es crear estructura en la tienda, y eso
              tiene que decirse antes y no después. */}
          {sugerencia.categoriasNuevas?.map((camino, i) => (
            <span
              key={i}
              className="mt-1.5 flex items-center gap-1.5 rounded-lg bg-neutral-50 py-1.5 px-2 text-[11px] text-neutral-600"
            >
              <FolderPlus className="w-3 h-3 shrink-0 text-neutral-400" />
              <span>
                Crear <span className="text-brand-black">{camino.join(' › ')}</span>
              </span>
            </span>
          ))}
          <span className="mt-1 block text-[11px] text-neutral-400">
            {actual.categorias.length === 0
              ? 'Ahora: sin categoría'
              : `Se suman a las ${actual.categorias.length} que ya tiene`}
          </span>
        </Casilla>
      )}

      {(sugerencia.peso || sugerencia.alto) && (
        <Casilla
          puesta={aceptados.includes('envio')}
          onCambiar={(v) => onCambiar('envio', v)}
          titulo="Peso y medidas"
        >
          <span className="mt-1 block text-xs text-neutral-600">
            {[
              sugerencia.peso && `${sugerencia.peso} kg`,
              sugerencia.alto &&
                `${sugerencia.alto} × ${sugerencia.ancho} × ${sugerencia.profundidad} cm`,
            ]
              .filter(Boolean)
              .join(' · ')}
          </span>
          {/* Por qué esas medidas. Es lo que deja darse cuenta de que se
              equivocó ANTES de que el correo cobre de más. */}
          {sugerencia.razonMedidas && (
            <span className="mt-1.5 flex items-start gap-1.5 rounded-lg bg-neutral-50 py-1.5 px-2 text-[11px] text-neutral-600">
              <Package className="mt-px w-3 h-3 shrink-0 text-neutral-400" />
              {sugerencia.razonMedidas}
            </span>
          )}
          <Antes valor={actual.peso ? `${actual.peso} kg` : null} />
        </Casilla>
      )}
    </div>
  );
}

export default function PanelDeIa({
  abierto,
  onCerrar,
  productos,
  trabajando,
  progreso,
  error,
  sugerencias,
  categorias,
  camposOfrecidos,
  onPedir,
  onAplicar,
}: {
  abierto: boolean;
  onCerrar: () => void;
  /** Uno o varios. El lote es el mismo panel con más filas. */
  productos: ProductoParaIa[];
  trabajando: boolean;
  /** "2 de 5" mientras corre el lote. `null` cuando es uno solo. */
  progreso: { hechos: number; total: number } | null;
  error: string | null;
  /** Vacío = todavía no se pidió nada. */
  sugerencias: Map<string, SugerenciaDeFicha>;
  categorias: CategoriaPlana[];
  /** Los campos que este destino admite. Los demás ni se ofrecen. */
  camposOfrecidos: readonly CampoIa[];
  onPedir: (campos: CampoIa[]) => void;
  /** Qué aceptó de cada producto. Los que no estén, no se tocan. */
  onAplicar: (aceptado: Map<string, CampoIa[]>) => void;
}) {
  const [pedidos, setPedidos] = useState<CampoIa[]>(() => porDefecto(camposOfrecidos));
  const [aceptados, setAceptados] = useState<Map<string, CampoIa[]>>(new Map());
  const [desplegado, setDesplegado] = useState<string | null>(null);
  const mensaje = useMensajeDeEspera(pedidos, trabajando);

  const hayRespuesta = sugerencias.size > 0;
  const varios = productos.length > 1;

  // Lo que vuelve arranca todo aceptado: el usuario ya pidió estos campos, y
  // destildar lo que no le gusta es menos trabajo que tildar lo que sí.
  useEffect(() => {
    if (sugerencias.size === 0) return;
    const m = new Map<string, CampoIa[]>();
    for (const [id, s] of sugerencias) {
      m.set(id, camposOfrecidos.filter((c) => pedidos.includes(c) && tieneAlgo(s, c)));
    }
    setAceptados(m);
    // Con un solo producto no hay nada que plegar.
    setDesplegado(productos.length === 1 ? (productos[0]?.id ?? null) : null);
    // Solo al llegar sugerencias nuevas.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sugerencias]);

  const cambiarCampo = (idProducto: string, campo: CampoIa, puesto: boolean) =>
    setAceptados((m) => {
      const siguiente = new Map(m);
      const actuales = siguiente.get(idProducto) ?? [];
      siguiente.set(idProducto, puesto ? [...actuales, campo] : actuales.filter((c) => c !== campo));
      return siguiente;
    });

  const conAlgo = productos.filter((p) => {
    const s = sugerencias.get(p.id);
    return s && camposOfrecidos.some((c) => tieneAlgo(s, c));
  });
  const totalAceptado = [...aceptados.values()].reduce((n, cs) => n + cs.length, 0);

  return (
    <SheetLateral
      open={abierto}
      onClose={onCerrar}
      titulo={varios ? `Completar ${productos.length} productos con IA` : 'Completar con IA'}
    >
      <div className="space-y-4 p-4">
        {!hayRespuesta && (
          <>
            <p className="text-xs leading-relaxed text-neutral-500">
              {varios
                ? `Mira las fotos de los ${productos.length} productos y propone estos datos para cada uno. No guarda nada: después vas a poder revisar producto por producto.`
                : 'Mira las fotos del producto y propone estos datos. No guarda nada: después vas a poder revisar cada uno antes de aceptarlo.'}
            </p>
            <div className="space-y-2">
              {camposOfrecidos.map((c) => (
                <Casilla
                  key={c}
                  puesta={pedidos.includes(c)}
                  onCambiar={(v) => setPedidos(v ? [...pedidos, c] : pedidos.filter((x) => x !== c))}
                  titulo={NOMBRE_DE_CAMPO[c]}
                >
                  {c === 'envio' && (
                    <span className="mt-1 block text-[11px] text-neutral-500">
                      Del PAQUETE, no del producto: una prenda se mide doblada, en su bolsa.
                    </span>
                  )}
                  {c === 'categorias' && (
                    <span className="mt-1 block text-[11px] text-neutral-500">
                      Usa las que ya armaste. Si ninguna sirve, propone crear una y te avisa
                      antes.
                    </span>
                  )}
                </Casilla>
              ))}
            </div>
            {error && <p className="rounded-xl bg-red-50 py-2 px-3 text-xs text-red-700">{error}</p>}
            <button
              type="button"
              disabled={pedidos.length === 0 || trabajando}
              onClick={() => onPedir(pedidos)}
              className={boton('primario', { extra: 'w-full py-3' })}
            >
              {trabajando ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4" />
              )}
              {trabajando ? (
                // `key` para que el fade se vuelva a correr en cada cambio: sin
                // remontar, la clase ya está aplicada y no pasa nada.
                <span key={mensaje} className="animate-paso-titulo">
                  {mensaje}
                  {progreso && ` · ${progreso.hechos} de ${progreso.total}`}
                </span>
              ) : (
                'Completar'
              )}
            </button>
          </>
        )}

        {hayRespuesta && (
          <>
            {conAlgo.length === 0 ? (
              <p className="rounded-xl border border-neutral-200 py-6 px-4 text-center text-sm text-neutral-500">
                No pudo deducir nada con seguridad de estas fotos. Probá con otras, o completá
                a mano.
              </p>
            ) : (
              <>
                <p className="text-xs leading-relaxed text-neutral-500">
                  Esto propone. Destildá lo que no quieras y tocá Aplicar: lo aceptado
                  reemplaza lo que haya en esos campos.
                </p>

                {conAlgo.map((p) => {
                  const s = sugerencias.get(p.id)!;
                  const suyos = aceptados.get(p.id) ?? [];
                  // Con uno solo no hay nada que plegar: la revisión va directa.
                  if (!varios) {
                    return (
                      <RevisionDeSugerencia
                        key={p.id}
                        sugerencia={s}
                        actual={p.actual}
                        aceptados={suyos}
                        onCambiar={(c, v) => cambiarCampo(p.id, c, v)}
                        categorias={categorias}
                      />
                    );
                  }
                  const abiertaEsta = desplegado === p.id;
                  return (
                    <div key={p.id} className="overflow-hidden rounded-xl border border-neutral-200">
                      <button
                        type="button"
                        onClick={() => setDesplegado(abiertaEsta ? null : p.id)}
                        className="flex w-full items-center gap-2 py-2.5 px-3 text-left hover:bg-neutral-50"
                      >
                        <ChevronRight
                          className={`w-4 h-4 shrink-0 text-neutral-400 transition-transform ${
                            abiertaEsta ? 'rotate-90' : ''
                          }`}
                        />
                        <span className="min-w-0 flex-1 truncate text-sm text-brand-black">
                          {p.nombre}
                        </span>
                        <span className="shrink-0 text-[11px] text-neutral-500">
                          {suyos.length} de {camposOfrecidos.filter((c) => tieneAlgo(s, c)).length}
                        </span>
                      </button>
                      {abiertaEsta && (
                        <div className="border-t border-neutral-100 p-3">
                          <RevisionDeSugerencia
                            sugerencia={s}
                            actual={p.actual}
                            aceptados={suyos}
                            onCambiar={(c, v) => cambiarCampo(p.id, c, v)}
                            categorias={categorias}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* Los que no devolvieron nada se dicen igual: si no, el usuario
                    cuenta cinco productos y ve cuatro, y no sabe qué pasó. */}
                {varios && conAlgo.length < productos.length && (
                  <p className="text-[11px] text-neutral-400">
                    {productos.length - conAlgo.length} producto
                    {productos.length - conAlgo.length === 1 ? '' : 's'} sin sugerencias: no pudo
                    deducir nada de sus fotos.
                  </p>
                )}
              </>
            )}

            {error && <p className="rounded-xl bg-red-50 py-2 px-3 text-xs text-red-700">{error}</p>}

            <div className="flex gap-2">
              <button
                type="button"
                onClick={onCerrar}
                className={boton('neutro', { extra: 'flex-1 py-3' })}
              >
                Descartar
              </button>
              <button
                type="button"
                disabled={totalAceptado === 0 || trabajando}
                onClick={() => onAplicar(aceptados)}
                className={boton('primario', { extra: 'flex-1 py-3' })}
              >
                {trabajando ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Check className="w-4 h-4" />
                )}
                Aplicar {totalAceptado > 0 && totalAceptado}
              </button>
            </div>
          </>
        )}
      </div>
    </SheetLateral>
  );
}
